import { z } from "zod";
import {
  AcceptanceCriterion,
  DodCheck,
  QaVerdict,
  SessionArtifactKind,
  SessionHandoff,
  TestStatus,
  ConflictChoice,
  DecisionAlternative,
  DecisionKind,
  DecisionUrgency,
  EnvironmentKind,
  GoalHealth,
  GoalScope,
  GoalStatus,
  KnowledgeSourceType,
  MilestoneStatus,
  PolicyAction,
  PolicyMode,
  ProjectKind,
  ProjectLifecycle,
  ProjectMemberRole,
  ProjectPriority,
  ReleaseStatus,
  TaskOrigin,
  TaskPriority,
  UploadStatus,
  UserRole,
  WorkflowConfig,
  ScheduleConfig,
  RoutineOutputKind,
  RoutineStatus,
  CapabilityRow,
  ConnectionGroup,
  ConnectionStatus,
  IncidentSeverity,
  IncidentSource,
  IncidentStatus,
  UiViewport,
} from "./domain.js";

type WithoutDefaults<S extends z.ZodRawShape> = {
  [K in keyof S]: S[K] extends z.ZodDefault<infer Inner> ? Inner : S[K];
};

/**
 * PATCH varianta create schématu. Holé `.partial()` nestačí: zod 4 doplní
 * `.default()` i u vynechaného volitelného pole, takže PATCH `{enabled:false}`
 * by přepsal ostatní pole výchozími hodnotami (prázdné DoD, stack, oddělení…).
 * Proto se defaulty nejdřív sundají a vynechané pole zůstane `undefined`.
 */
export function patchInput<S extends z.ZodRawShape>(schema: z.ZodObject<S>) {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [
      key,
      field instanceof z.ZodDefault ? field.unwrap() : field,
    ]),
  ) as WithoutDefaults<S>;
  return schema.extend(shape).partial();
}

/** Jednotný tvar API chyby. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// ---------- auth ----------

export const LoginInput = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof LoginInput>;

// ---------- users ----------

export const CreateUserInput = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email()),
  name: z.string().trim().min(1).max(120),
  role: UserRole,
  password: z.string().min(10).max(200),
});
export type CreateUserInput = z.infer<typeof CreateUserInput>;

export const UpdateUserInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: UserRole.optional(),
  disabled: z.boolean().optional(),
  password: z.string().min(10).max(200).optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserInput>;

// ---------- projects ----------

export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

export const CreateProjectInput = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z.string().regex(SLUG_REGEX, "slug: malá písmena, číslice a pomlčky").optional(),
  description: z.string().trim().max(2000).default(""),
  kind: ProjectKind.default("OTHER"),
  lifecycle: ProjectLifecycle.default("DISCOVERY"),
  businessPriority: ProjectPriority.default("NORMAL"),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const UpdateProjectInput = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().trim().max(2000).optional(),
  kind: ProjectKind.optional(),
  lifecycle: ProjectLifecycle.optional(),
  businessPriority: ProjectPriority.optional(),
  statusLine: z.string().trim().max(300).nullable().optional(),
  /** Důvod změny — propíše se do auditu (spec §59: reason summary). */
  reason: z.string().trim().max(500).optional(),
});
export type UpdateProjectInput = z.infer<typeof UpdateProjectInput>;

export const SetProjectMemberInput = z.object({
  userId: z.string().min(1),
  role: ProjectMemberRole,
});
export type SetProjectMemberInput = z.infer<typeof SetProjectMemberInput>;

// ---------- Fáze 2: applications ----------

export const CreateApplicationInput = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z.string().regex(SLUG_REGEX, "slug: malá písmena, číslice a pomlčky").optional(),
  description: z.string().trim().max(1000).default(""),
});
export type CreateApplicationInput = z.infer<typeof CreateApplicationInput>;

export const UpdateApplicationInput = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  description: z.string().trim().max(1000).optional(),
});
export type UpdateApplicationInput = z.infer<typeof UpdateApplicationInput>;

export const SetDesignCurrentInput = z.object({
  designVersionId: z.string().min(1),
});
export type SetDesignCurrentInput = z.infer<typeof SetDesignCurrentInput>;

// ---------- Fáze 2: uploads ----------

export const UpdateUploadInput = z.object({
  note: z.string().trim().max(500).nullable().optional(),
  status: UploadStatus.optional(),
});
export type UpdateUploadInput = z.infer<typeof UpdateUploadInput>;

// ---------- Fáze 2: knowledge ----------

/**
 * Název složky Knowledge — povoluje i diakritiku (workeři píší česky;
 * ASCII-only varianta tiše zahazovala extrakce do složek typu „Obchodní model").
 */
export const KNOWLEDGE_FOLDER_REGEX = /^[\p{L}\p{N}][\p{L}\p{N} _&.-]{0,48}$/u;

/**
 * Kdy si dokument načíst — spouštěč do rozcestníku workera. Prázdné pole není
 * detail formuláře: dokument bez spouštěče se do vaultu workera vůbec nedostane
 * (viz `buildVault`), takže vypadá založený, ale nikdo ho nikdy nepřečte.
 */
const READ_WHEN = z.string().trim().max(300);

export const CreateKnowledgeDocInput = z.object({
  folder: z.string().regex(KNOWLEDGE_FOLDER_REGEX, "Neplatný název složky"),
  title: z.string().trim().min(2).max(160),
  slug: z.string().regex(SLUG_REGEX).optional(),
  content: z.string().max(200_000),
  readWhen: READ_WHEN.optional(),
  changeSummary: z.string().trim().max(500).default("Vytvořeno"),
  sourceType: KnowledgeSourceType.default("HUMAN"),
  sourceUploadId: z.string().optional(),
  sourceRef: z.string().trim().max(200).optional(),
});
export type CreateKnowledgeDocInput = z.infer<typeof CreateKnowledgeDocInput>;

export const UpdateKnowledgeDocInput = z.object({
  title: z.string().trim().min(2).max(160).optional(),
  content: z.string().max(200_000),
  readWhen: READ_WHEN.optional(),
  /** Proč se pravda mění — povinné pro provenanci (spec §21). */
  changeSummary: z.string().trim().min(3).max(500),
  sourceType: KnowledgeSourceType.default("HUMAN"),
  sourceUploadId: z.string().optional(),
  sourceRef: z.string().trim().max(200).optional(),
});
export type UpdateKnowledgeDocInput = z.infer<typeof UpdateKnowledgeDocInput>;

/**
 * Samotný spouštěč, mimo revize. `readWhen` není změna pravdy — je to způsob,
 * jak se dokument nabízí — a psát kvůli němu revizi s nezměněným obsahem by
 * historii jen zašumělo. Stejně to dělá i worker endpoint.
 */
export const SetKnowledgeReadWhenInput = z.object({
  readWhen: READ_WHEN,
});
export type SetKnowledgeReadWhenInput = z.infer<typeof SetKnowledgeReadWhenInput>;

export const ChangeKnowledgeDocStatusInput = z.object({
  status: z.enum(["ACTIVE", "SUPERSEDED", "ARCHIVED"]),
  reason: z.string().trim().min(3).max(500),
});
export type ChangeKnowledgeDocStatusInput = z.infer<typeof ChangeKnowledgeDocStatusInput>;

const ConflictOptionInput = z.object({
  label: z.string().trim().min(1).max(200),
  detail: z.string().trim().max(1000).optional(),
  sourceRef: z.string().trim().max(200).optional(),
});

export const CreateKnowledgeConflictInput = z.object({
  docId: z.string().optional(),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().min(3).max(2000),
  optionA: ConflictOptionInput,
  optionB: ConflictOptionInput,
  recommended: z.enum(["A", "B"]).nullable().default(null),
});
export type CreateKnowledgeConflictInput = z.infer<typeof CreateKnowledgeConflictInput>;

export const ResolveKnowledgeConflictInput = z.object({
  choice: ConflictChoice,
  resolutionNote: z.string().trim().max(1000).optional(),
  /** Nový obsah dokumentu po rozhodnutí (vytvoří revizi se sourceType DECISION). */
  newContent: z.string().max(200_000).optional(),
});
export type ResolveKnowledgeConflictInput = z.infer<typeof ResolveKnowledgeConflictInput>;

// ---------- Fáze 2: codebases ----------

export const CreateCodebaseInput = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z.string().regex(SLUG_REGEX).optional(),
  description: z.string().trim().max(1000).default(""),
  repoUrl: z.string().trim().max(300).nullable().optional(),
  defaultBranch: z.string().trim().min(1).max(100).default("main"),
  devBranch: z.string().trim().max(100).nullable().optional(),
  branchPolicy: z.string().trim().max(300).nullable().optional(),
  prPolicy: z.string().trim().max(300).nullable().optional(),
  testPolicy: z.string().trim().max(300).nullable().optional(),
  techStack: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
  dodChecks: z.array(DodCheck).max(20).default([]),
  applicationIds: z.array(z.string()).max(50).default([]),
  environmentIds: z.array(z.string()).max(20).default([]),
});
export type CreateCodebaseInput = z.infer<typeof CreateCodebaseInput>;

export const UpdateCodebaseInput = patchInput(CreateCodebaseInput).omit({ slug: true });
export type UpdateCodebaseInput = z.infer<typeof UpdateCodebaseInput>;

export const SetCodebaseDependencyInput = z.object({
  toCodebaseId: z.string().min(1),
  label: z.string().trim().max(100).nullable().optional(),
});
export type SetCodebaseDependencyInput = z.infer<typeof SetCodebaseDependencyInput>;

// ---------- Fáze 2: environments ----------

export const CreateEnvironmentInput = z.object({
  key: z.string().regex(SLUG_REGEX).optional(),
  name: z.string().trim().min(2).max(80),
  kind: EnvironmentKind,
  url: z.string().trim().max(300).nullable().optional(),
  deployPolicy: PolicyMode.default("APPROVAL"),
  /** Git větev environmentu (staging=beta, production=main). */
  branch: z.string().trim().max(100).nullable().optional(),
});
export type CreateEnvironmentInput = z.infer<typeof CreateEnvironmentInput>;

export const UpdateEnvironmentInput = patchInput(CreateEnvironmentInput).omit({ key: true });
export type UpdateEnvironmentInput = z.infer<typeof UpdateEnvironmentInput>;

// ---------- Fáze 2: policies ----------

export const UpdateProjectPoliciesInput = z.object({
  policies: z.partialRecord(PolicyAction, PolicyMode),
  reason: z.string().trim().max(500).optional(),
});
export type UpdateProjectPoliciesInput = z.infer<typeof UpdateProjectPoliciesInput>;

// ---------- Fáze 2: onboarding ----------

export const OnboardProjectInput = z.object({
  name: z.string().trim().min(2).max(120),
  slug: z.string().regex(SLUG_REGEX).optional(),
  description: z.string().trim().max(2000).default(""),
  kind: ProjectKind.default("OTHER"),
  templateKey: z.string().max(60).optional(),
  lifecycle: ProjectLifecycle.default("DISCOVERY"),
  businessPriority: ProjectPriority.default("NORMAL"),
  knowledgeFolders: z.array(z.string().regex(KNOWLEDGE_FOLDER_REGEX)).max(20).optional(),
  applications: z
    .array(
      z.object({
        name: z.string().trim().min(2).max(120),
        slug: z.string().regex(SLUG_REGEX).optional(),
        description: z.string().trim().max(1000).default(""),
      }),
    )
    .max(30)
    .default([]),
  codebases: z
    .array(
      z.object({
        name: z.string().trim().min(2).max(120),
        slug: z.string().regex(SLUG_REGEX).optional(),
        description: z.string().trim().max(1000).default(""),
        repoUrl: z.string().trim().max(300).nullable().optional(),
        defaultBranch: z.string().trim().min(1).max(100).default("main"),
        techStack: z.array(z.string().trim().min(1).max(40)).max(20).default([]),
        /** Sluggy aplikací z tohoto onboarding payloadu, ke kterým se codebase váže. */
        applicationSlugs: z.array(z.string()).max(30).default([]),
      }),
    )
    .max(30)
    .default([]),
  environments: z
    .array(
      z.object({
        key: z.string().regex(SLUG_REGEX).optional(),
        name: z.string().trim().min(2).max(80),
        kind: EnvironmentKind,
        url: z.string().trim().max(300).nullable().optional(),
        deployPolicy: PolicyMode.default("APPROVAL"),
        branch: z.string().trim().max(100).nullable().optional(),
      }),
    )
    .max(20)
    .default([]),
  policies: z.partialRecord(PolicyAction, PolicyMode).optional(),
  /** Klíče oddělení z globálního registru (spec §7), která si projekt vybírá.
   * Volnější než SLUG_REGEX — builtin klíče („qa") mají jen 2 znaky; existenci ověří server. */
  departments: z.array(z.string().trim().min(1).max(64)).max(30).default([]),
  /** Úvodní project goals — zjednodušená podoba UpsertGoalInput (scope/projectId doplní server). */
  goals: z
    .array(
      z.object({
        title: z.string().trim().min(3).max(200),
        description: z.string().trim().max(2000).default(""),
        metric: z.string().trim().max(120).default(""),
        targetValue: z.number().nullable().optional(),
        unit: z.string().trim().max(20).default(""),
        deadline: z.iso.datetime().nullable().optional(),
      }),
    )
    .max(20)
    .default([]),
  /** Úvodní milestone projektu (viz UpsertMilestoneInput). */
  milestone: z
    .object({
      title: z.string().trim().min(2).max(160),
      note: z.string().trim().max(500).default(""),
      deadline: z.iso.datetime().nullable().optional(),
    })
    .nullable()
    .optional(),
  /** Integrace k připojení — klíč šablony (github/railway/…) nebo vlastní slug; secrets až po vzniku. */
  connections: z
    .array(
      z.object({
        key: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,40}$/),
        name: z.string().trim().min(2).max(80).optional(),
        note: z.string().trim().max(500).default(""),
      }),
    )
    .max(20)
    .default([]),
});
export type OnboardProjectInput = z.infer<typeof OnboardProjectInput>;

// ---------- Fáze 3: departments & workflows ----------

export const UpsertDepartmentInput = z.object({
  key: z.string().regex(SLUG_REGEX).optional(),
  name: z.string().trim().min(2).max(80),
  color: z.enum(["cyan", "green", "amber", "red", "violet", "dim"]).default("cyan"),
  description: z.string().trim().max(500).default(""),
  purpose: z.string().trim().max(500).default(""),
  basePrompt: z.string().trim().max(4000).default(""),
  modelHint: z.string().trim().max(60).nullable().optional(),
  capabilities: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
  toolsNote: z.string().trim().max(300).default(""),
  autonomyNote: z.string().trim().max(300).default(""),
  workflowKey: z.string().min(1),
  enabled: z.boolean().default(true),
});
export type UpsertDepartmentInput = z.infer<typeof UpsertDepartmentInput>;

export const UpdateDepartmentInput = patchInput(UpsertDepartmentInput);
export type UpdateDepartmentInput = z.infer<typeof UpdateDepartmentInput>;

export const UpdateWorkflowInput = WorkflowConfig.extend({
  name: z.string().trim().min(2).max(80).optional(),
});
export type UpdateWorkflowInput = z.infer<typeof UpdateWorkflowInput>;

export const SetProjectDepartmentsInput = z.object({
  departmentIds: z.array(z.string()).max(30),
});
export type SetProjectDepartmentsInput = z.infer<typeof SetProjectDepartmentsInput>;

export const UpdateThemeInput = z.object({
  theme: z.enum(["DARK", "LIGHT", "SYSTEM"]),
});
export type UpdateThemeInput = z.infer<typeof UpdateThemeInput>;

// ---------- Fáze 3: tasks ----------

/**
 * Platforma stroje, na kterém jde task udělat. Null/undefined = kdekoli.
 * Claim task nabídne jen workerovi se shodným OS (z runnerPlatform "os/arch").
 */
export const TaskPlatform = z.enum(["darwin", "win32", "linux"]);
export type TaskPlatform = z.infer<typeof TaskPlatform>;

export const CreateTaskInput = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(10_000).default(""),
  departmentId: z.string().nullable().optional(),
  parentId: z.string().nullable().optional(),
  priority: TaskPriority.default("P2"),
  deadline: z.iso.datetime().nullable().optional(),
  milestoneId: z.string().nullable().optional(),
  initiativeId: z.string().nullable().optional(),
  acceptance: z.array(z.string().trim().min(1).max(500)).max(30).default([]),
  applicationIds: z.array(z.string()).max(30).default([]),
  codebaseIds: z.array(z.string()).max(30).default([]),
  origin: TaskOrigin.default("SCOPE"),
  requiresPlatform: TaskPlatform.optional(),
});
export type CreateTaskInput = z.infer<typeof CreateTaskInput>;

export const UpdateTaskInput = z.object({
  title: z.string().trim().min(3).max(200).optional(),
  description: z.string().trim().max(10_000).optional(),
  departmentId: z.string().nullable().optional(),
  priority: TaskPriority.optional(),
  deadline: z.iso.datetime().nullable().optional(),
  milestoneId: z.string().nullable().optional(),
  initiativeId: z.string().nullable().optional(),
  releaseId: z.string().nullable().optional(),
  acceptance: z.array(AcceptanceCriterion).max(30).optional(),
  applicationIds: z.array(z.string()).max(30).optional(),
  codebaseIds: z.array(z.string()).max(30).optional(),
  requiresPlatform: TaskPlatform.nullable().optional(),
});
export type UpdateTaskInput = z.infer<typeof UpdateTaskInput>;

export const MoveTaskInput = z.object({
  status: z.string().min(1).max(40),
  reason: z.string().trim().max(300).optional(),
});
export type MoveTaskInput = z.infer<typeof MoveTaskInput>;

export const TaskDependencyInput = z.object({
  dependsOnTaskId: z.string().min(1),
  note: z.string().trim().max(200).optional(),
});
export type TaskDependencyInput = z.infer<typeof TaskDependencyInput>;

export const TaskCommentInput = z.object({
  text: z.string().trim().min(1).max(2000),
});
export type TaskCommentInput = z.infer<typeof TaskCommentInput>;

// ---------- Fáze 3: decisions & proposals ----------

export const CreateDecisionInput = z.object({
  projectId: z.string().nullable().optional(),
  kind: DecisionKind.default("QUESTION"),
  title: z.string().trim().min(3).max(300),
  context: z.string().trim().max(4000).default(""),
  recommendation: z.string().trim().max(1000).default(""),
  reasoning: z.string().trim().max(4000).default(""),
  alternatives: z.array(DecisionAlternative).max(10).default([]),
  impact: z.string().trim().max(1000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
  blockedTaskIds: z.array(z.string()).max(50).default([]),
});
export type CreateDecisionInput = z.infer<typeof CreateDecisionInput>;

export const ResolveDecisionInput = z.object({
  /** "RECOMMENDED" | "REJECTED" | klíč alternativy. */
  resolution: z.string().min(1).max(60),
  note: z.string().trim().max(1000).optional(),
});
export type ResolveDecisionInput = z.infer<typeof ResolveDecisionInput>;

/**
 * Úkol pro člověka. Vědomě NEMÁ `alternatives` ani `recommendation` — kdyby je
 * měl, byl by to rozhodnutí. Má naopak `whyHuman` (proč to worker nemůže
 * udělat) a `expectedFinding` (co má člověk zjistit).
 */
export const CreateHumanTaskInput = z.object({
  projectId: z.string().nullable().optional(),
  title: z.string().trim().min(3).max(300),
  context: z.string().trim().max(4000).default(""),
  whyHuman: z.string().trim().max(1000).default(""),
  expectedFinding: z.string().trim().max(1000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
  blockedTaskIds: z.array(z.string()).max(50).default([]),
});
export type CreateHumanTaskInput = z.infer<typeof CreateHumanTaskInput>;

/**
 * Uzavření úkolu nálezem. `finding` je povinný a je to OBSAH, ne potvrzení:
 * u „zkontroluj vzhled a diakritiku na páskách" je odpověď člověka přímo tím,
 * co worker potřebuje k dopsání AC. Prázdné „hotovo" by úkol zahodilo.
 */
export const CompleteHumanTaskInput = z.object({
  finding: z.string().trim().min(2).max(4000),
});
export type CompleteHumanTaskInput = z.infer<typeof CompleteHumanTaskInput>;

/** Úkol se dělat nebude — důvod jde workerovi stejnou cestou jako nález. */
export const DismissHumanTaskInput = z.object({
  reason: z.string().trim().min(2).max(1000),
});
export type DismissHumanTaskInput = z.infer<typeof DismissHumanTaskInput>;

/** Vyřízení návrhu na vlastní obrazovce — bez oklikou přes rozhodnutí. */
export const DecideProposalInput = z.object({
  decision: z.enum(["APPROVE", "REJECT"]),
  note: z.string().trim().max(1000).optional(),
});
export type DecideProposalInput = z.infer<typeof DecideProposalInput>;

/**
 * Přeřazení otevřeného rozhodnutí do správné kategorie. Vzniklo kvůli tomu, že
 * v inboxu leží položky, které rozhodnutí nikdy nebyly (kontrola pásek,
 * připojení čtečky). Automatika by je přeřadila špatně, takže se přeřazují
 * jednotlivě a vědomě — původní rozhodnutí se přitom nemaže, uzavře se
 * s odkazem na to, kam obsah odešel.
 */
export const ReclassifyDecisionInput = z.object({
  target: z.enum(["HUMAN_TASK", "PROPOSAL"]),
  note: z.string().trim().max(1000).optional(),
});
export type ReclassifyDecisionInput = z.infer<typeof ReclassifyDecisionInput>;

export const CreateProposalInput = z.object({
  projectId: z.string().min(1),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(4000).default(""),
  rationale: z.string().trim().max(2000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
  proposedByLabel: z.string().trim().max(80).default("AI"),
});
export type CreateProposalInput = z.infer<typeof CreateProposalInput>;

// ---------- Fáze 3: goals / milestones / releases ----------

export const UpsertGoalInput = z.object({
  scope: GoalScope,
  projectId: z.string().nullable().optional(),
  parentGoalId: z.string().nullable().optional(),
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(2000).default(""),
  metric: z.string().trim().max(120).default(""),
  targetValue: z.number().nullable().optional(),
  currentValue: z.number().nullable().optional(),
  unit: z.string().trim().max(20).default(""),
  deadline: z.iso.datetime().nullable().optional(),
  health: GoalHealth.default("ON_TRACK"),
  progress: z.number().int().min(0).max(100).optional(),
  ownerLabel: z.string().trim().max(80).default(""),
  status: GoalStatus.default("ACTIVE"),
});
export type UpsertGoalInput = z.infer<typeof UpsertGoalInput>;

export const UpdateGoalInput = patchInput(UpsertGoalInput);
export type UpdateGoalInput = z.infer<typeof UpdateGoalInput>;

export const CreateInitiativeInput = z.object({
  title: z.string().trim().min(3).max(200),
  note: z.string().trim().max(500).default(""),
});
export type CreateInitiativeInput = z.infer<typeof CreateInitiativeInput>;

export const UpsertMilestoneInput = z.object({
  title: z.string().trim().min(2).max(160),
  note: z.string().trim().max(500).default(""),
  deadline: z.iso.datetime().nullable().optional(),
  status: MilestoneStatus.default("OPEN"),
});
export type UpsertMilestoneInput = z.infer<typeof UpsertMilestoneInput>;

export const UpdateMilestoneInput = patchInput(UpsertMilestoneInput);
export type UpdateMilestoneInput = z.infer<typeof UpdateMilestoneInput>;

export const UpsertReleaseInput = z.object({
  version: z.string().trim().min(1).max(40),
  name: z.string().trim().max(120).default(""),
  notes: z.string().trim().max(2000).default(""),
  status: ReleaseStatus.default("DRAFT"),
});
export type UpsertReleaseInput = z.infer<typeof UpsertReleaseInput>;

export const UpdateReleaseInput = patchInput(UpsertReleaseInput);
export type UpdateReleaseInput = z.infer<typeof UpdateReleaseInput>;

// ---------- Fáze 4: workers (lidské API) ----------

export const CreateWorkerInput = z.object({
  name: z.string().trim().min(2).max(80),
  capabilities: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
  departmentIds: z.array(z.string()).max(30).default([]),
  /// Prázdné = všechny projekty; jinak worker claimuje jen tasky/routiny těchto projektů.
  projectIds: z.array(z.string()).max(100).default([]),
  /// Předplatné stroje (§7.2) — z něj se počítá cena spotřeby v korunách.
  billingAccount: z.string().trim().max(80).optional(),
  billingTariff: z.string().trim().max(40).optional(),
  billingPrice: z.number().min(0).max(1_000_000).optional(),
  billingCurrency: z.string().trim().max(8).optional(),
});
export type CreateWorkerInput = z.infer<typeof CreateWorkerInput>;

export const UpdateWorkerInput = patchInput(CreateWorkerInput).extend({
  enabled: z.boolean().optional(),
});
export type UpdateWorkerInput = z.infer<typeof UpdateWorkerInput>;

// ---------- Fáze 4: worker connector ----------

export const WorkerCapabilitiesInput = z.object({
  capabilities: z.array(z.string().trim().min(1).max(40)).max(30),
});
export type WorkerCapabilitiesInput = z.infer<typeof WorkerCapabilitiesInput>;

export const SessionDiscussionInput = z.object({
  text: z.string().trim().min(1).max(2000),
});
export type SessionDiscussionInput = z.infer<typeof SessionDiscussionInput>;

/**
 * Limity předplatného stroje, jak je claude CLI posílá v `rate_limit_event`
 * (stejná data, jaká ukazuje `/usage`). Runner je vyčte z výstupu, který už
 * parsuje, takže je hlášení zadarmo. Hodnoty se nevalidují proti výčtu —
 * přibude-li typ limitu, ať ho pošle a UI ho ukáže, než se doplní překlad.
 */
export const WorkerRateLimitInput = z.object({
  status: z.string().trim().max(40).optional(),
  type: z.string().trim().max(60).optional(),
  resetsAt: z.string().datetime().optional(),
  overage: z.boolean().optional(),
  /// Vyčerpaná procenta z `claude -p "/usage"` — 5h okno, týden přes všechny
  /// modely a týdenní limit konkrétního modelu (`weekModelName`, např. Opus).
  /// Nad 100 nejdou, ale strop je volnější pro případ změny na straně CLI.
  sessionPct: z.number().min(0).max(1000).optional(),
  weekPct: z.number().min(0).max(1000).optional(),
  weekModelPct: z.number().min(0).max(1000).optional(),
  weekModelName: z.string().trim().max(40).optional(),
});
export type WorkerRateLimitInput = z.infer<typeof WorkerRateLimitInput>;

/**
 * Identita stroje: verze runneru, platforma a verze Claude Code CLI. Posílá se
 * v heartbeatu, ne jednorázově při startu — po aktualizaci runneru se tak stav
 * v UI srovná sám, bez zvláštního volání.
 */
export const WorkerRunnerInput = z.object({
  version: z.string().trim().max(40).optional(),
  platform: z.string().trim().max(40).optional(),
  claudeVersion: z.string().trim().max(40).optional(),
});
export type WorkerRunnerInput = z.infer<typeof WorkerRunnerInput>;

export const WorkerHeartbeatInput = z.object({
  sessionId: z.string().optional(),
  currentActivity: z.string().trim().max(200).optional(),
  progress: z.number().int().min(0).max(100).optional(),
  /// Posílá runner i mezi tasky — limit je vlastnost stroje, ne session.
  rateLimit: WorkerRateLimitInput.optional(),
  /// Verze runneru a CLI — taky vlastnost stroje, taky i mezi tasky.
  runner: WorkerRunnerInput.optional(),
});
export type WorkerHeartbeatInput = z.infer<typeof WorkerHeartbeatInput>;

/**
 * Skutečná spotřeba session, jak ji naměřil runner workera (`usage` v result
 * eventu claude CLI). Model svou spotřebu nezná — dokud ji hlásil sám, chodily
 * nuly. Přijímá se i po finish, protože runner ji zná až po skončení procesu.
 */
export const SessionUsageInput = z.object({
  tokensInput: z.number().int().min(0).optional(),
  tokensOutput: z.number().int().min(0).optional(),
  tokensCacheRead: z.number().int().min(0).optional(),
  tokensCacheWrite: z.number().int().min(0).optional(),
  costUsd: z.number().min(0).optional(),
  /// Počet kroků (požadavků na model) cyklu. S `tokensCacheRead` dává průměrnou
  /// velikost kontextu na krok — jediné číslo, ze kterého jde poznat, jestli
  /// session platila hodně práce, nebo jen mnohokrát totéž.
  turns: z.number().int().min(0).max(100000).optional(),
  models: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
  /// Kolik procent limitu session spotřebovala — rozdíl `/usage` před a po.
  /// Jinak to spočítat nelze: kapacita limitu se v tokenech neuvádí. Pozor,
  /// limity jsou účtu, ne stroje: běží-li na jednom účtu víc workerů naráz,
  /// je v čísle i jejich spotřeba.
  weekPctSpent: z.number().min(0).max(100).optional(),
  sessionPctSpent: z.number().min(0).max(100).optional(),
});
export type SessionUsageInput = z.infer<typeof SessionUsageInput>;

export const SessionPlanInput = z.object({
  steps: z.array(z.string().trim().min(1).max(300)).min(1).max(40),
});
export type SessionPlanInput = z.infer<typeof SessionPlanInput>;

export const SessionCheckpointInput = z.object({
  summary: z.string().trim().min(3).max(500),
  /** Stavy kroků plánu v pořadí (TODO/DOING/DONE) — celé pole. */
  planStates: z.array(z.enum(["TODO", "DOING", "DONE"])).max(40).optional(),
  progress: z.number().int().min(0).max(100).optional(),
  currentActivity: z.string().trim().max(200).optional(),
  models: z.array(z.string().trim().max(80)).max(10).optional(),
  tokensInput: z.number().int().min(0).optional(),
  tokensOutput: z.number().int().min(0).optional(),
});
export type SessionCheckpointInput = z.infer<typeof SessionCheckpointInput>;

export const SessionArtifactInput = z.object({
  kind: SessionArtifactKind,
  label: z.string().trim().min(1).max(200),
  ref: z.string().trim().max(400).optional(),
});
export type SessionArtifactInput = z.infer<typeof SessionArtifactInput>;

export const SessionDelegateTaskInput = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(4000).default(""),
  departmentKey: z.string().optional(),
  acceptance: z.array(z.string().trim().min(1).max(500)).max(20).default([]),
  /** Nový task blokuje aktuální (frontend čeká na backend, spec §29). */
  blocksCurrent: z.boolean().default(false),
  /** Subtask aktuálního tasku (parent = aktuální task). */
  asSubtaskOfCurrent: z.boolean().default(false),
  /**
   * Priorita nového tasku. Bez ní task dědí prioritu tasku, ze kterého se
   * deleguje — to sedí u drobné delegace, ale ne u rozpadu velkého zadání,
   * kde má část tasků běžet dřív než rodič. Když worker rozpadá práci na
   * desítky tasků, musí umět říct, co je P0 a co P3.
   */
  priority: TaskPriority.optional(),
  /**
   * Čísla tasků ve stejném projektu, na kterých má nový task čekat. Vzniknou
   * z nich závislosti (nový task → uvedené tasky), takže nedokončená závislost
   * ho rovnou zablokuje. Bez toho neumí worker rozpadnout práci do vrstev,
   * které na sebe navazují — což 15. 9. 2026 zastavilo rozpad redesignu
   * CashRegisteru na 76 tasků (blokace u #489).
   */
  dependsOnTaskNumbers: z.array(z.number().int().positive()).max(30).default([]),
  /**
   * Platforma, bez které task nejde udělat (např. tauri dev na macOS). Claim
   * ho pak nabídne jen workerovi na tom OS — popis tasku ani handoff to
   * nevynutí, zjistil to Windows worker třikrát za sebou (Macbook HUB #43).
   */
  requiresPlatform: TaskPlatform.optional(),
  /**
   * Přesný text položky checklistu `Product/Plán vývoje`, ze které task
   * vzniká. Dokončení tasku ji v plánu automaticky odškrtne — plán tak drží
   * krok s realitou i mezi koly „Doplnit plán práce".
   */
  planItem: z.string().trim().max(300).optional(),
});
export type SessionDelegateTaskInput = z.infer<typeof SessionDelegateTaskInput>;

export const SessionDecisionInput = z.object({
  title: z.string().trim().min(3).max(300),
  context: z.string().trim().max(4000).default(""),
  recommendation: z.string().trim().max(1000).default(""),
  reasoning: z.string().trim().max(4000).default(""),
  alternatives: z.array(DecisionAlternative).max(10).default([]),
  impact: z.string().trim().max(1000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
  blocksCurrent: z.boolean().default(true),
});
export type SessionDecisionInput = z.infer<typeof SessionDecisionInput>;

/**
 * Úkol pro člověka založený workerem. Rozdíl proti `SessionDecisionInput` je
 * v testu, ne ve formátu: rozhodnutí má aspoň dvě varianty a volba ownera mění,
 * co se postaví; úkol má výsledek předem jasný a jen ho nemůže udělat nikdo
 * jiný než člověk. Proto tu nejsou `alternatives` — kdyby byly, patří to jinam.
 */
export const SessionHumanTaskInput = z.object({
  title: z.string().trim().min(3).max(300),
  context: z.string().trim().max(4000).default(""),
  /** Proč to nemůže udělat worker (fyzický zásah, hardware, chybějící přístup). */
  whyHuman: z.string().trim().max(1000).default(""),
  /** Co má člověk zjistit a napsat zpět — dostaneš to jako vstup. */
  expectedFinding: z.string().trim().max(1000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
  blocksCurrent: z.boolean().default(true),
});
export type SessionHumanTaskInput = z.infer<typeof SessionHumanTaskInput>;

export const SessionProposalInput = z.object({
  title: z.string().trim().min(3).max(200),
  description: z.string().trim().max(4000).default(""),
  rationale: z.string().trim().max(2000).default(""),
  urgency: DecisionUrgency.default("NORMAL"),
});
export type SessionProposalInput = z.infer<typeof SessionProposalInput>;

/** Worker označí podklad za zpracovaný do Knowledge (spec §18). */
export const SessionUploadProcessedInput = z.object({
  /** ID tasků založených z podkladu (delegací) — smí být prázdné s vysvětlením v note. */
  taskIds: z.array(z.string()).max(30).default([]),
  note: z.string().trim().max(500).optional(),
});
export type SessionUploadProcessedInput = z.infer<typeof SessionUploadProcessedInput>;

/** Worker zapisuje strukturovanou Knowledge (spec §17) — upsert dle folder+title. */
export const SessionKnowledgeInput = z.object({
  folder: z.string().regex(KNOWLEDGE_FOLDER_REGEX, "Neplatný název složky"),
  title: z.string().trim().min(2).max(160),
  content: z.string().max(200_000),
  changeSummary: z.string().trim().min(3).max(500),
  /** Upload, ze kterého pravda pochází — provenance (spec §21). */
  sourceUploadId: z.string().optional(),
  /**
   * Kdy si dokument načíst — jedna věta popisující situaci („hledáš, kde je
   * fakturace"). Podle ní se dokument nabízí v rozcestníku vaultu; bez ní se
   * do rozcestníku nedostane a worker o něm neví.
   */
  readWhen: z.string().trim().max(300).optional(),
  /**
   * Mapa kódu: commit, proti kterému je dokument ověřený, a codebase, které se
   * týká. Bez commitu nejde poznat, jestli mapa ještě platí — a mapa, která
   * lže, je horší než žádná: worker podle ní jde špatně a pak stejně hledá.
   */
  codeCommit: z.string().trim().max(64).optional(),
  codebaseSlug: z.string().trim().max(64).optional(),
});
export type SessionKnowledgeInput = z.infer<typeof SessionKnowledgeInput>;

/**
 * Worker odloží vlastní Knowledge dokument (spec §17): duplicitu, kterou sám
 * založil, nebo dokument, který nahradil jiným. Mazat Knowledge nesmí nikdo —
 * je to auditovaná paměť firmy — ale bez možnosti dokument odložit workerovi
 * nezbývalo než ho vyprázdnit, což v seznamu nechá prázdného ducha.
 */
export const SessionKnowledgeArchiveInput = z.object({
  /** Proč se odkládá. Povinné — bez důvodu je z archivu za měsíc hádanka. */
  reason: z.string().trim().min(3).max(500),
  /** Dokument, který ho nahrazuje (folder/slug). Když sedí, stav je SUPERSEDED. */
  supersededBySlug: z.string().trim().max(160).optional(),
});
export type SessionKnowledgeArchiveInput = z.infer<typeof SessionKnowledgeArchiveInput>;

/**
 * Worker (systémový task „Doplnit plán práce") označí projekt jako připravený
 * k produkci — vše z Knowledge implementované, žádná otevřená práce, poslední
 * testovací kolo bez nálezů. Owner rozhoduje o nasazení sám.
 */
export const SessionFactoryReadyInput = z.object({
  reason: z.string().trim().min(10).max(1000),
});
export type SessionFactoryReadyInput = z.infer<typeof SessionFactoryReadyInput>;

export const SessionFinishInput = z.object({
  outcome: z.enum(["COMPLETED", "FAILED", "PAUSED"]),
  summary: z.string().trim().min(3).max(1000),
  handoff: SessionHandoff.optional(),
  models: z.array(z.string().trim().max(80)).max(10).optional(),
  tokensInput: z.number().int().min(0).optional(),
  tokensOutput: z.number().int().min(0).optional(),
  /** Cílový stav tasku (validuje se workflow); výchozí dle outcome. */
  moveTo: z.string().max(40).optional(),
});
export type SessionFinishInput = z.infer<typeof SessionFinishInput>;

// ---------- Fáze 5: QA ----------

export const QaVerdictInput = z.object({
  verdict: QaVerdict,
  note: z.string().trim().max(1000).default(""),
});
export type QaVerdictInput = z.infer<typeof QaVerdictInput>;

export const TestReportInput = z.object({
  runs: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(200),
        status: TestStatus,
        checkKey: z.string().trim().max(40).optional(),
        codebase: z.string().trim().max(80).optional(),
        detail: z.string().trim().max(1000).optional(),
        statsTotal: z.number().int().min(0).optional(),
        statsPassed: z.number().int().min(0).optional(),
      }),
    )
    .min(1)
    .max(30),
});
export type TestReportInput = z.infer<typeof TestReportInput>;

export const WorkerQaVerdictInput = QaVerdictInput.extend({
  /** Task, kterého se verdikt týká — výchozí je task session (QA agent může verdiktovat související dev task). */
  taskId: z.string().optional(),
  /**
   * Alternativa k `taskId`: číslo tasku v projektu. Plán, Knowledge i diskuze
   * mluví výhradně v `#123`, ale cuid se worker odjinud než z `qaQueue`
   * nedozví — a tasky, které gate už opustily, v ní nejsou. Bez tohohle pole
   * dostal QA task zadání, které nešlo splnit, a session skončila PAUSED.
   */
  taskNumber: z.number().int().positive().optional(),
});
export type WorkerQaVerdictInput = z.infer<typeof WorkerQaVerdictInput>;

// ---------- routines (Fáze 6) ----------

export const CreateRoutineInput = z.object({
  name: z.string().trim().min(2).max(120),
  departmentId: z.string().nullable().optional(),
  schedule: ScheduleConfig,
  instructions: z.string().trim().min(1).max(4000),
  autonomy: z.string().trim().max(1000).default(""),
  outputs: z.array(RoutineOutputKind).min(1).max(4),
  capabilities: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  modelHint: z.string().trim().max(60).nullable().optional(),
  projectIds: z.array(z.string()).max(50).default([]),
});
export type CreateRoutineInput = z.infer<typeof CreateRoutineInput>;

export const UpdateRoutineInput = patchInput(CreateRoutineInput).extend({
  status: RoutineStatus.optional(),
});
export type UpdateRoutineInput = z.infer<typeof UpdateRoutineInput>;

/** Výstup běhu routine (spec §38) — worker vytváří přes run endpoint. */
export const RoutineOutputInput = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("TASK"),
    projectId: z.string(),
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().max(4000).default(""),
    departmentKey: z.string().optional(),
    priority: TaskPriority.default("P2"),
    acceptance: z.array(z.string().trim().min(1).max(300)).max(20).default([]),
  }),
  z.object({
    kind: z.literal("PROPOSAL"),
    projectId: z.string(),
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().min(1).max(4000),
    rationale: z.string().trim().max(2000).default(""),
  }),
  z.object({
    kind: z.literal("DECISION"),
    projectId: z.string(),
    title: z.string().trim().min(3).max(200),
    context: z.string().trim().min(1).max(4000),
    recommendation: z.string().trim().max(1000).default(""),
    urgency: DecisionUrgency.default("NORMAL"),
  }),
  z.object({
    kind: z.literal("REPORT"),
    title: z.string().trim().min(3).max(200),
    content: z.string().trim().min(1).max(20000),
  }),
  z.object({
    kind: z.literal("INCIDENT"),
    projectId: z.string(),
    title: z.string().trim().min(3).max(200),
    severity: IncidentSeverity.default("P2"),
    summary: z.string().trim().max(4000).default(""),
    dedupeKey: z.string().trim().max(200).optional(),
  }),
]);
export type RoutineOutputInput = z.infer<typeof RoutineOutputInput>;

/**
 * Blokace: **něco brání dokončení tasku**. Nic jiného.
 *
 * Dřív se sem hlásilo i to, co worker obešel a task přesto dokončil — proto
 * jich na produkci leželo 130 otevřených, z toho 124 na tasku ve stavu DONE.
 * Takové hlášení patří do `SessionWorkaroundInput`.
 */
export const SessionBlockerInput = z.object({
  /** Co brání dokončení. Stabilní formulace — je to klíč pro slučování i dedupe. */
  reason: z.string().trim().min(3).max(500),
  detail: z.string().trim().max(2000).optional(),
  /// Překážku může odstranit jen člověk (přístup, credentials, fyzický zásah) —
  /// server k blokaci rovnou založí úkol pro člověka a jeho uzavřením blokaci
  /// sám odbaví. NE rozhodnutí: „připoj čtečku" nemá varianty.
  ///
  /// **Zároveň to task VYŘADÍ Z FRONTY.** Bez toho si ho claim za minutu vezme
  /// znovu a další cyklus jen zjistí totéž — #282 takhle spálil dva cykly na
  /// zjištění, že role do tiskárny pořád nedorazila.
  ownerAction: z.boolean().optional(),
  /// Zablokovat aktuální task. Výchozí se odvozuje od `ownerAction`: co může
  /// odstranit jen člověk, task drží; co si worker vyřeší sám, ne.
  blocksCurrent: z.boolean().optional(),
});
export type SessionBlockerInput = z.infer<typeof SessionBlockerInput>;

/**
 * Obchvat: worker něco **neudělal, přeskočil nebo obešel — a task přesto
 * dokončil**. Není to blokace: blokace je jen to, co brání dokončení tasku.
 *
 * Hlásí se proto, aby šlo analyzovat, co v AppFactory nebo v AppFactoryWorker
 * upravit, aby to příště obcházet nemusel. Nikdy z toho nevzniká otevřená
 * položka pro ownera — je to podklad k analýze, ne úkol.
 */
export const SessionWorkaroundInput = z.object({
  /** Co jsi neudělal / přeskočil / obešel. Stabilní formulace — podle ní se shlukuje. */
  reason: z.string().trim().min(3).max(500),
  /** Čím jsi to nahradil a co z toho neplatí. */
  detail: z.string().trim().max(2000).optional(),
  /**
   * Co by to příště nevyžadovalo — chybějící nástroj ve workerovi, chybějící
   * endpoint Factory, pomalý deploy… Tohle pole dělá z hromady poznámek
   * seznam kandidátů na úpravu; bez něj se z obchvatů nedá nic vyvodit.
   */
  whatWouldHelp: z.string().trim().max(1000).optional(),
});
export type SessionWorkaroundInput = z.infer<typeof SessionWorkaroundInput>;

/**
 * Vyřešení blokace workerem: `note` říká, čím překážka pominula a jak to
 * worker ověřil (ne „asi ok"). Vyřešená blokace zmizí z centrálního seznamu.
 */
/**
 * Poučení do Factory brainu. Nepovinné schválně — laťka je v tom, že ho někdo
 * vědomě naformuluje. Bez něj se z odbavené blokace založí jen kandidát, který
 * čeká na doplnění člověkem.
 */
export const WorkerLessonInput = z.object({
  title: z.string().trim().min(5).max(120),
  /// Kdy si poznámku načíst — spouštěč vyhledání. Bez něj je nenalezitelná.
  readWhen: z.string().trim().max(300).optional(),
  /// Markdown; odkazy na jiné poznámky přes [[slug]].
  body: z.string().trim().min(10).max(8000),
  /// FACTORY = platí na všech projektech. Povýšení je vědomý krok.
  scope: z.enum(["FACTORY", "PROJECT"]).optional(),
});
export type WorkerLessonInput = z.infer<typeof WorkerLessonInput>;

export const WorkerBlockerResolveInput = z.object({
  note: z.string().trim().min(3).max(1000),
  /// Co si z téhle překážky odnést do příště (viz WorkerLessonInput).
  lesson: WorkerLessonInput.optional(),
});
export type WorkerBlockerResolveInput = z.infer<typeof WorkerBlockerResolveInput>;

/**
 * Worker nahlásí hosting environmentů (nové projekty Coolify, dobíhající
 * Railway): bez tohoto hlášení Factory nevidí buildy a Releases neukazují,
 * co je kde nasazené. Idempotentní upsert dle environment key.
 *
 * Obecná pole jsou `provider` + `hostingProjectId`/`hostingEnvironmentId`/
 * `services[].hostingServiceId`. Starý tvar `railway*` (worker se starým
 * manuálem) se přijímá jako alias — bez `provider` znamená Railway.
 */
const HOSTING_ID = z.string().trim().min(1).max(64);

/** Alias `railwayX` → `hostingX`, když obecné pole chybí. */
function aliasRailway(raw: unknown, pairs: [string, string][]): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const out: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
  for (const [general, legacy] of pairs) {
    if (out[general] === undefined && out[legacy] !== undefined) out[general] = out[legacy];
    delete out[legacy];
  }
  return out;
}

export const HostingProviderInput = z.preprocess(
  (v) => (typeof v === "string" ? v.trim().toUpperCase() : v),
  z.enum(["RAILWAY", "COOLIFY"]),
);

export const SessionDeployConfigInput = z.object({
  environments: z
    .array(
      z.preprocess(
        (raw) =>
          aliasRailway(raw, [
            ["hostingProjectId", "railwayProjectId"],
            ["hostingEnvironmentId", "railwayEnvironmentId"],
          ]),
        z.object({
          /** Key environmentu projektu (staging/production). */
          key: z.string().trim().min(1).max(40),
          /** Kde environment běží; bez udání Railway (zpětná kompatibilita). */
          provider: HostingProviderInput.default("RAILWAY"),
          /** Railway project ID (UUID) / uuid Coolify projektu. */
          hostingProjectId: HOSTING_ID,
          /** Railway environment ID (UUID) / název prostředí v Coolify (production/staging). */
          hostingEnvironmentId: HOSTING_ID,
          /** Veřejná URL environmentu (vygenerovaná doména). */
          url: z.string().trim().url().max(300).optional(),
          services: z
            .array(
              z.preprocess(
                (raw) => aliasRailway(raw, [["hostingServiceId", "railwayServiceId"]]),
                z.object({
                  codebaseSlug: z.string().trim().min(1).max(80),
                  /** Railway service ID (UUID) / uuid Coolify aplikace. */
                  hostingServiceId: HOSTING_ID,
                  /** Veřejná URL service v environmentu (vygenerovaná/custom doména). */
                  url: z.string().trim().url().max(300).optional(),
                }),
              ),
            )
            .max(30)
            .default([]),
        }),
      ),
    )
    .min(1)
    .max(10),
});
export type SessionDeployConfigInput = z.infer<typeof SessionDeployConfigInput>;

/**
 * Worker nahlásí skutečné umístění kódu codebase — typicky když repo od
 * ownera chybí/není přístupné a worker založil vlastní pod svým GitHub
 * účtem (hosting nasazuje jen repa, ke kterým má jeho GitHub app přístup). Skutečné
 * umístění kódu nikdy nesmí zůstat nezapsané ve Factory.
 */
export const SessionCodebaseRepoInput = z.object({
  codebaseSlug: z.string().trim().min(1).max(80),
  repoUrl: z.string().trim().url().max(300),
  defaultBranch: z.string().trim().min(1).max(80).optional(),
  devBranch: z.string().trim().min(1).max(80).optional(),
  /** Proč se repo změnilo (např. „owner repo neexistuje, založeno vlastní"). */
  note: z.string().trim().max(500).optional(),
});
export type SessionCodebaseRepoInput = z.infer<typeof SessionCodebaseRepoInput>;

/** Incident objevený během task session (spec §30/§62). */
export const SessionIncidentInput = z.object({
  title: z.string().trim().min(3).max(200),
  severity: IncidentSeverity.default("P2"),
  summary: z.string().trim().max(4000).default(""),
  dedupeKey: z.string().trim().max(200).optional(),
});
export type SessionIncidentInput = z.infer<typeof SessionIncidentInput>;

export const RoutineRunFinishInput = z.object({
  status: z.enum(["COMPLETED", "FAILED"]),
  summary: z.string().trim().min(1).max(500),
  models: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
  tokensInput: z.number().int().min(0).optional(),
  tokensOutput: z.number().int().min(0).optional(),
});
export type RoutineRunFinishInput = z.infer<typeof RoutineRunFinishInput>;

// ---------- connections & incidents (Fáze 7) ----------

export const CreateConnectionInput = z.object({
  /** Klíč šablony (github/railway/…) nebo vlastní slug. */
  key: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,40}$/),
  name: z.string().trim().min(2).max(80).optional(),
  icon: z.string().trim().max(4).optional(),
  group: ConnectionGroup.optional(),
  departmentsNote: z.string().trim().max(200).optional(),
  note: z.string().trim().max(500).default(""),
  capabilities: z.array(CapabilityRow).max(30).optional(),
});
export type CreateConnectionInput = z.infer<typeof CreateConnectionInput>;

export const UpdateConnectionInput = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  group: ConnectionGroup.optional(),
  status: ConnectionStatus.optional(),
  departmentsNote: z.string().trim().max(200).optional(),
  note: z.string().trim().max(500).optional(),
  capabilities: z.array(CapabilityRow).max(30).optional(),
});
export type UpdateConnectionInput = z.infer<typeof UpdateConnectionInput>;

/** Nastavení secret hodnoty — hodnota se nikdy nevrací (spec §58). */
export const SetSecretInput = z.object({
  name: z.string().trim().regex(/^[a-zA-Z0-9_.-]{1,60}$/),
  value: z.string().min(1).max(4000),
});
export type SetSecretInput = z.infer<typeof SetSecretInput>;

/**
 * Coolify server, na kterém běží projekty (vlastní i jiných uživatelů).
 * Token se jen zapisuje — nikdy se nevrací (DTO nese hint).
 */
export const HostingServerInput = z.object({
  name: z.string().trim().min(1).max(60),
  /** REST API serveru, např. https://server.indracorp.cz/api/v1. */
  apiUrl: z
    .string()
    .trim()
    .url()
    .max(300)
    .transform((v) => v.replace(/\/+$/, "")),
  /** Coolify API token (read/write/deploy); vynechaný = beze změny. */
  token: z.string().trim().min(8).max(500).optional(),
  note: z.string().trim().max(500).optional(),
  /** Výchozí server pro projekty bez přiřazení — nastavuje jen OWNER. */
  isDefault: z.boolean().optional(),
  /** Vlastník serveru (userId) — jiného než sebe nastavuje jen OWNER. */
  ownerId: z.string().trim().min(1).max(40).nullable().optional(),
});
export type HostingServerInput = z.infer<typeof HostingServerInput>;

export const UpdateHostingServerInput = patchInput(HostingServerInput);
export type UpdateHostingServerInput = z.infer<typeof UpdateHostingServerInput>;

/** Přiřazení serveru projektu; null = výchozí server Factory. */
export const SetProjectHostingServerInput = z.object({
  serverId: z.string().trim().min(1).max(40).nullable(),
});
export type SetProjectHostingServerInput = z.infer<typeof SetProjectHostingServerInput>;

export const CreateIncidentInput = z.object({
  title: z.string().trim().min(3).max(200),
  severity: IncidentSeverity,
  summary: z.string().trim().max(4000).default(""),
  source: IncidentSource.default("MANUAL"),
  errorRate: z.string().trim().max(60).nullable().optional(),
  affectedUsers: z.string().trim().max(120).nullable().optional(),
  businessImpact: z.string().trim().max(200).nullable().optional(),
  departmentKey: z.string().optional(),
  dedupeKey: z.string().trim().max(200).optional(),
});
export type CreateIncidentInput = z.infer<typeof CreateIncidentInput>;

export const UpdateIncidentInput = z.object({
  status: IncidentStatus.optional(),
  severity: IncidentSeverity.optional(),
  errorRate: z.string().trim().max(60).nullable().optional(),
  affectedUsers: z.string().trim().max(120).nullable().optional(),
  businessImpact: z.string().trim().max(200).nullable().optional(),
  linkedDecisionId: z.string().nullable().optional(),
  /** Poznámka k timeline (vytvoří event). */
  note: z.string().trim().max(1000).optional(),
});
export type UpdateIncidentInput = z.infer<typeof UpdateIncidentInput>;

export const ResolveIncidentInput = z.object({
  note: z.string().trim().min(1).max(2000),
  /** Založit post-mortem task pro Operations (výchozí u P0/P1). */
  postMortem: z.boolean().optional(),
});
export type ResolveIncidentInput = z.infer<typeof ResolveIncidentInput>;

/** Monitoring ingest (Sentry-kompatibilní duch) — auth přes ingest token secret. */
export const IngestMonitoringInput = z.object({
  projectSlug: z.string().trim().min(1),
  title: z.string().trim().min(3).max(200),
  detail: z.string().trim().max(4000).default(""),
  severity: IncidentSeverity.default("P2"),
  source: IncidentSource.default("MONITORING"),
  errorRate: z.string().trim().max(60).optional(),
  affectedUsers: z.string().trim().max(120).optional(),
  businessImpact: z.string().trim().max(200).optional(),
  dedupeKey: z.string().trim().max(200).optional(),
});
export type IngestMonitoringInput = z.infer<typeof IngestMonitoringInput>;

/**
 * Sentry proxy pro workera (spec §57/§62): token integrace zůstává na serveru,
 * worker jen říká, co chce udělat. Zavření issue je zápis — capability matice
 * ho pouští teprve po přepnutí na AUTO.
 */
export const WorkerSentryResolveInput = z.object({
  /** Resolve „in next release" — Sentry issue znovu otevře, když se chyba vrátí. */
  inNextRelease: z.boolean().default(false),
  /** Čím je oprava ověřená (jde do auditu a timeline incidentu). */
  note: z.string().trim().max(500).default(""),
});
export type WorkerSentryResolveInput = z.infer<typeof WorkerSentryResolveInput>;

/**
 * Zapojení monitoringu pro jeden codebase. Factory má jeden Sentry token pro
 * celou firmu — worker jen řekne, který repozitář se má hlídat.
 */
export const WorkerSentryProvisionInput = z.object({
  codebaseSlug: z.string().trim().min(1).max(80),
  /** Platforma pro Sentry (`node`, `javascript-react`…) — jen kosmetika v UI Sentry. */
  platform: z.string().trim().max(60).optional(),
});
export type WorkerSentryProvisionInput = z.infer<typeof WorkerSentryProvisionInput>;

// ---------- jarvis (Fáze 8) ----------

export const JarvisChatInput = z.object({
  message: z.string().trim().min(1).max(4000),
  /** Posledních ~12 zpráv konverzace (server je stateless, spec §67). */
  history: z
    .array(z.object({ role: z.enum(["user", "assistant"]), content: z.string().max(8000) }))
    .max(24)
    .default([]),
  /** Kde v UI uživatel právě je — Jarvis mluví v kontextu (spec §71). */
  currentView: z.string().max(200).optional(),
});
export type JarvisChatInput = z.infer<typeof JarvisChatInput>;

export const JarvisConfirmInput = z.object({
  tool: z.string().min(1).max(60),
  args: z.record(z.string(), z.unknown()).default({}),
});
export type JarvisConfirmInput = z.infer<typeof JarvisConfirmInput>;

export const JarvisTtsInput = z.object({
  text: z.string().trim().min(1).max(2000),
});
export type JarvisTtsInput = z.infer<typeof JarvisTtsInput>;

// ---------- Pitch Perfect & feedback ----------

export const SCREEN_KEY_REGEX = /^[a-z0-9][a-z0-9/_-]{0,120}$/;

/** Lidský verdikt nad screenshotem obrazovky (Pitch Perfect smyčka). */
export const ReviewScreenInput = z.object({
  verdict: z.enum(["APPROVED", "CHANGES_REQUESTED", "PENDING"]),
  /** Obecná připomínka — u CHANGES_REQUESTED povinná, pokud nejsou viewportNotes. */
  note: z.string().trim().max(4000).optional(),
  /** Připomínky k jednotlivým viewportům (desktop OK, mobil ne). */
  viewportNotes: z
    .array(z.object({ viewport: UiViewport, note: z.string().trim().min(1).max(2000) }))
    .max(3)
    .optional(),
});
export type ReviewScreenInput = z.infer<typeof ReviewScreenInput>;

/** Metadata screenshotu nahrávaného workerem (multipart pole `meta`). */
export const SessionScreenshotMeta = z.object({
  /** Stabilní klíč obrazovky, např. "admin/login" nebo "web/checkout-modal". */
  key: z.string().regex(SCREEN_KEY_REGEX, "key: malá písmena, číslice, /, _, -"),
  title: z.string().trim().min(2).max(160).optional(),
  kind: z.enum(["PAGE", "MODAL", "FORM", "EMAIL", "COMPONENT", "OTHER"]).default("PAGE"),
  /**
   * Viewport screenshotu. U PAGE/MODAL/FORM je povinná trojice
   * DESKTOP+MOBILE+TABLET — bez ní server nepustí DoD check `screens`.
   */
  viewport: z.enum(["DESKTOP", "MOBILE", "TABLET"]).default("DESKTOP"),
  routePath: z.string().trim().max(300).nullable().optional(),
  applicationSlug: z.string().trim().max(80).optional(),
  /** Co se od minulé verze změnilo. */
  note: z.string().trim().max(1000).optional(),
  /**
   * "true" = skutečná podoba obrazovky se změnila a screenshot ji nahrazuje —
   * jediný případ, kdy jde přepsat i APPROVED screen (status → PENDING,
   * člověk dělá nové review). Vyžaduje note s popisem změny.
   */
  uiChanged: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});
export type SessionScreenshotMeta = z.infer<typeof SessionScreenshotMeta>;

/** Metadata obrazové přílohy tasku (multipart pole vedle `file`). */
export const SessionTaskAttachmentMeta = z.object({
  /** K čemu příloha je („screenshot rozbitého steppera na mobilu“). */
  note: z.string().trim().max(500).optional(),
});
export type SessionTaskAttachmentMeta = z.infer<typeof SessionTaskAttachmentMeta>;

/** Worker označí připomínku (screen/feedback) za zpracovanou do tasků. */
export const FeedbackProcessedInput = z.object({
  /** ID tasků vytvořených z připomínky (delegací) — smí být prázdné s vysvětlením v note. */
  taskIds: z.array(z.string()).max(30).default([]),
  note: z.string().trim().max(1000).optional(),
});
export type FeedbackProcessedInput = z.infer<typeof FeedbackProcessedInput>;

/** Owner píše připomínku do projektového feedbacku (JSON varianta bez přílohy). */
export const CreateFeedbackInput = z.object({
  text: z.string().trim().min(1).max(8000),
});
export type CreateFeedbackInput = z.infer<typeof CreateFeedbackInput>;

// ---------- audit ----------

export const AuditQuery = z.object({
  actorType: z.enum(["HUMAN", "AI", "WORKER", "SYSTEM"]).optional(),
  projectId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AuditQuery = z.infer<typeof AuditQuery>;

// ---------- settings ----------

/** Fáze 1: obecné key/value nastavení Factory (typované klíče). */
export const SettingKey = z.enum(["factory.name", "factory.timezone"]);
export type SettingKey = z.infer<typeof SettingKey>;

export const UpdateSettingsInput = z.object({
  // partialRecord: klient smí poslat jen měněné klíče
  settings: z.partialRecord(SettingKey, z.string().max(500)),
});
export type UpdateSettingsInput = z.infer<typeof UpdateSettingsInput>;

// ---------- osobní API tokeny (MCP konektor) ----------

export const CreateApiTokenInput = z.object({
  name: z.string().trim().min(2).max(80),
  /** Platnost ve dnech; bez hodnoty token nevyprší. */
  expiresInDays: z.number().int().min(1).max(3650).optional(),
});
export type CreateApiTokenInput = z.infer<typeof CreateApiTokenInput>;
